import { SlideHeader } from "../../../SlideHeader";

const CAPABILITIES: Array<{
  capability: string;
  delivery: string;
  adjacent: string;
}> = [
  {
    capability: "Daily expert audit",
    delivery:
      "Latest ASO best practices applied daily to produce ranked, plain-language recommendations for the next revision cycle.",
    adjacent: "AppTweak (Atlas AI), Asodesk Optimizer, App Radar",
  },
  {
    capability: "Competitor monitoring",
    delivery:
      "Every move competitors make on their listings — metadata, rankings, asset changes — surfaced the day it happens.",
    adjacent: "AppTweak, Sensor Tower, Mobile Action",
  },
  {
    capability: "AI/LLM discoverability",
    delivery:
      "Visibility monitoring across ChatGPT, Claude, Gemini, Perplexity, plus Reddit and web probing — the new app-discovery surfaces.",
    adjacent:
      "LLM Pulse, AthenaHQ, Otterly — brand/web visibility only; none audit apps",
  },
  {
    capability: "Automated localization",
    delivery:
      "High-quality translations of revisions for every supported market, grounded in per-market localization analysis.",
    adjacent: "Lokalise, Phrase, Crowdin",
  },
  {
    capability: "Store Analytics",
    delivery:
      "App Store Connect and Google Play Console performance — conversion rates, install velocity — attributed to every shipped change.",
    adjacent: "Appfigures, AppsFlyer, EasyAppReports",
  },
  {
    capability: "Integrated publishing",
    delivery:
      "One-click ship of the revision to App Store and Google Play, no copy-paste, no manual sync.",
    adjacent: "App Radar, Asodesk, ASO.dev",
  },
  {
    capability: "Review sentiment analysis",
    delivery:
      "Continuous sentiment scoring of iOS and Google Play reviews, distilled into product themes and actionable advice.",
    adjacent: "AppFollow, Appbot, Mobile Action",
  },
  {
    capability: "AI/LLM content generation",
    delivery:
      "Text and image generation for store metadata and creative assets.",
    adjacent: "ChatGPT, Claude, Midjourney",
  },
];

export default function PlatformBreadth() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:pl-24 md:pr-20 md:pt-16 md:pb-12 md:[&_header]:mb-6">
      <SlideHeader unnumbered eyebrow="Appendix B · Tool Consolidation">
        <h1 className="text-2xl font-semibold leading-tight tracking-tight md:text-4xl md:leading-[1.15]">
          Apptonomy unifies what you'd otherwise stitch from{" "}
          <span className="text-blue-700">8+ point solutions</span>.
        </h1>
        <p className="mt-3 text-base text-slate-600 md:mt-4 md:text-lg">
          Each capability area has its incumbents. Only Apptonomy turns all
          eight into one continuously-running ASO loop.
        </p>
      </SlideHeader>

      <div className="grid grid-cols-1 gap-3 md:flex-1 md:grid-cols-2 md:gap-x-6 md:gap-y-3">
        {CAPABILITIES.map((cap) => (
          <CapabilityRow key={cap.capability} {...cap} />
        ))}
      </div>

      <div className="mt-5 border-t border-slate-200 pt-4 md:mt-6 md:pt-5">
        <p className="text-balance text-center text-sm leading-snug text-slate-700 md:text-base">
          When all eight feed the same audit, every revision cycle's
          recommendations get sharper.{" "}
          <span className="font-semibold text-slate-900">
            The integration compounds. The point solutions don't.
          </span>
        </p>
      </div>
    </div>
  );
}

function CapabilityRow({
  capability,
  delivery,
  adjacent,
}: {
  capability: string;
  delivery: string;
  adjacent: string;
}) {
  return (
    <div className="rounded-lg bg-white p-3 ring-1 ring-slate-200 md:p-4">
      <p className="text-sm font-semibold text-slate-900 md:text-base">
        {capability}
      </p>
      <p className="mt-1 text-xs leading-snug text-slate-700 md:text-sm">
        {delivery}
      </p>
      <p className="mt-2 text-sm leading-snug text-slate-500 md:text-base">
        <span className="italic">e.g.,</span>{" "}
        <span className="font-medium text-slate-700">{adjacent}</span>
      </p>
    </div>
  );
}
